import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { pngDateTime } from '../utils/format.js'

/**
 * useAuthenticatedImage — fetch a JWT-gated image endpoint and expose it
 * as a Blob URL suitable for a native <img src>.
 *
 * Why: PR B (2026-07-09) moved the Clinical Identification Photo behind a
 * role-gated endpoint (GET /api/patients/<id>/identification-photo/) that
 * enforces can_view_identification_photo per request. Endpoint requires a
 * JWT Authorization header, which <img> tags can't send by themselves.
 * The workaround is standard: fetch via the authenticated axios client,
 * receive an image blob, wrap it in URL.createObjectURL, hand THAT to the
 * <img>. Object URL is scoped to the current page; revoking it on unmount
 * releases the memory and prevents the image from being surfaced to a
 * later, un-authenticated context.
 *
 * Returns the current Blob URL (or empty string while loading / errored)
 * and a boolean isLoading flag callers can use to render a placeholder.
 */
function useAuthenticatedImage(url) {
  const [blobUrl, setBlobUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!url) {
      setBlobUrl('')
      return
    }
    let cancelled = false
    let objectUrl = ''
    setIsLoading(true)

    api.get(url, { responseType: 'blob' })
      .then((resp) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(resp.data)
        setBlobUrl(objectUrl)
      })
      .catch(() => {
        if (cancelled) return
        // Silent fail — the card renders the initials fallback. The
        // most common cause is a role-gate denial (404), which is a
        // valid state, not an error to surface to the clinician.
        setBlobUrl('')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
      // Free the browser's blob memory + revoke the URL so any late
      // <img> render can't fetch it after unmount. Standard hygiene.
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url])

  return { blobUrl, isLoading }
}

/**
 * PatientIdentityCard — compact identity tile with photo + confidence badge.
 *
 * Reused across surfaces where a clinician needs a visual identity check:
 *   * Encounter editor header (next to demographics)
 *   * Pharmacy dispensing modal (before releasing medication)
 *   * Lab specimen-collection modal
 *   * Radiology positioning screen
 *   * Ambulance dispatch view
 *   * Reception check-in
 *
 * Deliberately NOT rendered on queue lists — only on the action screen
 * where identity confirmation matters. See docs/design/patient-identity-
 * verification.md :: "Where the thumbnail appears".
 *
 * Confidence display is driven by identity_confidence_display coming from
 * the serializer ("High Confidence" / "Moderate Confidence" / "Unverified").
 * The FE never hard-codes internal status strings — the label + colour
 * come from the API to keep the state machine authoritative on the backend.
 */

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] || ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return (first + last).toUpperCase()
}

function ConfidenceBadge({ display }) {
  const color =
    display === 'High Confidence'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : display === 'Moderate Confidence'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-rose-50 text-rose-800 border-rose-200'
  const icon =
    display === 'High Confidence' ? '✓' :
    display === 'Moderate Confidence' ? '•' :
    '⚠'
  return (
    <span
      className={
        'inline-flex items-center gap-1 text-[10px] uppercase tracking-wide ' +
        'font-semibold border rounded-full px-2 py-0.5 ' + color
      }
    >
      <span>{icon}</span>
      <span>{display || 'Unverified'}</span>
    </span>
  )
}

export default function PatientIdentityCard({
  patient,
  size = 'md',        // 'sm' | 'md' | 'lg'
  showBadge = true,
  showVerifiedBy = true,
  className = '',
}) {
  if (!patient) return null

  const {
    full_name,
    identification_photo_url,
    identity_confidence_display,
    identity_verified_by_name,
    identity_verified_at,
  } = patient

  const dims = size === 'sm' ? 'w-10 h-10 text-xs' :
               size === 'lg' ? 'w-24 h-24 text-2xl' :
                              'w-14 h-14 text-base'

  // Format via the shared PNG timezone helper (utils/format.js) so this
  // date is consistent with every other timestamp in the clinical UI —
  // otherwise "verified at 8pm UTC" ends up rendering one day ahead of
  // "encounter updated" on the same page.
  const verifiedAt = identity_verified_at ? pngDateTime(identity_verified_at) : ''

  // PR B (2026-07-09): identification_photo_url is now a JWT-gated
  // endpoint, not a directly-loadable HMAC URL. Fetch it as a blob via
  // the authenticated axios client and render the resulting Object URL.
  // Falls back to initials if the fetch fails (typically a role-gate
  // denial rendered as 404 by the endpoint).
  const { blobUrl } = useAuthenticatedImage(identification_photo_url)

  return (
    <div className={'flex items-center gap-3 ' + className}>
      {blobUrl ? (
        <img
          src={blobUrl}
          alt={full_name ? `Identification photo of ${full_name}` : 'Identification photo'}
          className={
            'rounded-full object-cover border border-slate-200 ' +
            'ring-2 ring-white shadow-sm ' + dims
          }
        />
      ) : (
        <div
          className={
            'rounded-full flex items-center justify-center ' +
            'bg-slate-100 text-slate-600 font-semibold border border-slate-200 ' + dims
          }
          aria-label="No identification photo on file"
        >
          {initials(full_name)}
        </div>
      )}

      <div className="min-w-0">
        {showBadge && (
          <ConfidenceBadge display={identity_confidence_display} />
        )}
        {showVerifiedBy && identity_verified_by_name && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            Verified by {identity_verified_by_name}
            {verifiedAt && ` · ${verifiedAt}`}
          </div>
        )}
      </div>
    </div>
  )
}
